/**
 * Cálculo de espaço para as listas ancoradas do Design System (`Select`,
 * `Combobox`).
 *
 * Fica em arquivo próprio por dois motivos: um módulo de componente que exporta
 * constantes quebra o Fast Refresh do Vite, e a conta é a mesma nos dois
 * primitivos — duplicá-la garantiria que um dia só um dos dois receberia a
 * correção.
 */

export type EspacoDoPopover = {
  /** A lista abre para cima porque não cabia para baixo. */
  paraCima: boolean;
  /** Altura máxima do painel, em px. */
  alturaMax: number;
};

/** Altura de uma opção (`min-h-12`). */
export const ALTURA_DA_OPCAO = 48;

/**
 * Decide se a lista abre para baixo ou para cima, e de quanto ela pode ser.
 *
 * Sem isso, um campo perto do rodapé abria uma lista que saía da tela: as
 * últimas opções ficavam inalcançáveis e o usuário nem via que existiam.
 *
 * O limite é o container que rola, não a janela. Dentro de um modal — que é
 * onde todo filtro do produto vive — medir pela janela faz a lista crescer além
 * do painel: ela passa por cima dos campos de baixo e some cortada na borda da
 * área rolável.
 */
export const medirEspacoDoPopover = (gatilho: HTMLElement): EspacoDoPopover => {
  const RESPIRO = 8; // folga até a borda da janela
  const MINIMO = 160; // abaixo disso não cabem nem três opções
  const TETO = 320;

  const recorte = (() => {
    let elemento = gatilho.parentElement;
    while (elemento) {
      const estilo = getComputedStyle(elemento);
      if (/(auto|scroll|hidden)/.test(estilo.overflowY)) {
        return elemento.getBoundingClientRect();
      }
      elemento = elemento.parentElement;
    }
    return null;
  })();

  const limiteSuperior = recorte ? Math.max(0, recorte.top) : 0;
  const limiteInferior = recorte
    ? Math.min(window.innerHeight, recorte.bottom)
    : window.innerHeight;

  const { top, bottom } = gatilho.getBoundingClientRect();
  const abaixo = limiteInferior - bottom - RESPIRO;
  const acima = top - limiteSuperior - RESPIRO;
  // Só inverte se, além de faltar espaço embaixo, sobrar mais em cima.
  const paraCima = abaixo < MINIMO && acima > abaixo;
  const disponivel = paraCima ? acima : abaixo;

  return {
    paraCima,
    alturaMax: Math.min(TETO, Math.max(MINIMO, disponivel)),
  };
};

/**
 * Altura da área rolável de opções — que termina no MEIO de uma opção, de
 * propósito.
 *
 * Uma lista que acaba certinha na borda da última opção visível parece
 * completa, e o usuário não desconfia que há mais coisa abaixo. Meia opção
 * cortada é o único sinal que funciona sem depender de barra de rolagem — que
 * no macOS só aparece quando o dedo já está rolando.
 *
 * @param alturaMax Altura disponível para o painel inteiro.
 * @param reservado Altura de cabeçalhos do painel (campo de busca, por exemplo).
 */
export const alturaDaListaVisivel = (
  alturaMax: number,
  reservado = 0
): number => {
  const disponivel = alturaMax - reservado - 8; // padding da lista
  const meia = ALTURA_DA_OPCAO / 2;
  // Quantas opções INTEIRAS cabem deixando ainda a meia de baixo aparecendo.
  // Dividir o espaço todo por 48 daria uma conta maior que o espaço, e o
  // contêiner cortaria a meia de volta.
  const inteiras = Math.max(
    1,
    Math.floor((disponivel - meia) / ALTURA_DA_OPCAO)
  );
  return inteiras * ALTURA_DA_OPCAO + meia;
};

/** Compara sem acento e sem caixa: quem procura "canaa" tem de achar "Canaã". */
export const semAcento = (texto: string): string =>
  texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
